<template>
  <div class="share">
    <slot :togglePopup="togglePopup" />

    <transition name="slide-bottom">
      <div v-show="isOpen" ref="tooltipEl" class="share__popup">
        <ButtonClose class="share__close" @click="closePopup" />

        <h2 class="share__headline">{{ $t('feature.share.headline') }}</h2>

        <ul class="share__links-list">
          <ButtonBase
            v-for="({ icon, name }, id) in enabledServices"
            :key="id"
            class="share__link"
            :class="`share__link--${name}`"
            :title="$t('feature.share.shareVia', { service: name })"
            @click="shareVia(id)"
          >
            <div class="share__link-inner">
              <component :is="icon" class="share__link-icon" />{{ name }}
            </div>
          </ButtonBase>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import ButtonBase from '../ButtonBase';
import ButtonClose from '../ButtonClose';

import IconEmail from '../../assets/icons/mail.svg?inline';
import IconFacebook from '../../assets/icons/facebook.svg?inline';
// import IconInstagram from '../../assets/icons/instagram.svg?inline';
import IconLinkedIn from '../../assets/icons/linkedin.svg?inline';
import IconSMS from '../../assets/icons/sms.svg?inline';
import IconTwitter from '../../assets/icons/twitter.svg?inline';
import IconWhatsapp from '../../assets/icons/whatsapp.svg?inline';

// http://whoknew.dk/programmatically-opening-a-new-tab-window-on-mobile-safari-55.htm
function openShareWindow(url) {
  // Create link in memory
  const a = window.document.createElement('a');
  a.target = '_blank';
  a.href = url;

  // Dispatch fake click
  const e = window.document.createEvent('MouseEvents');
  e.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
  a.dispatchEvent(e);
}

const serviceIds = {
  EMAIL: 'email',
  FACEBOOK: 'facebook',
  // INSTAGRAM: 'instagram', // sharing via instagram is not easily possible
  LINKED_IN: 'linkedIn',
  PINTEREST: 'pinterest',
  SMS: 'sms',
  TWITTER: 'twitter',
  WHATSAPP: 'whatsapp',
};

const enabledServiceIds = [
  serviceIds.LINKED_IN,
  serviceIds.FACEBOOK,
  serviceIds.EMAIL,
  serviceIds.SMS,
  serviceIds.TWITTER,
  serviceIds.WHATSAPP,
];

export default {
  name: 'Share',

  components: {
    ButtonBase,
    ButtonClose,
  },

  data() {
    return {
      isOpen: false,
    };
  },

  computed: {
    shareUrl() {
      // current url without hash
      const defaultShareUrl = location
        .toString()
        .replace(location.search, '')
        .replace(location.hash, '');

      return this.$t('meta.urlShort') || defaultShareUrl;
    },

    allServices() {
      const meta = {
        shareUrl: this.shareUrl,
        imgFacebook: process.env.VUE_APP_SHARE_IMAGE,
        imgPinterest: '',
        title: this.$t('meta.shareTitle'),
        description: this.$t('meta.siteDescription'),
        descriptionShort: this.$t('meta.siteDescriptionShort'),
        hashTags: '', // part of description
        subject: this.$t('meta.shareSubject', { title: this.$t('meta.shareTitle') }),
        body: this.$t('meta.shareBody', {
          title: this.$t('meta.siteDescription'),
          shareUrl: this.shareUrl,
        }),
      };

      // encode values
      Object.entries(meta).forEach(([id, value]) => {
        meta[id] = encodeURIComponent(value);
      });

      // https://github.com/bradvin/social-share-urls/
      return {
        [serviceIds.EMAIL]: {
          name: this.$t('feature.share.service.email'),
          url: `mailto:?subject=${meta.subject}&body=${meta.body}`,
          icon: IconEmail,
          via: 'location',
        },
        [serviceIds.FACEBOOK]: {
          name: this.$t('feature.share.service.facebook'),
          url: `https://www.facebook.com/sharer.php?u=${meta.shareUrl}&title=${meta.title}&description=${meta.description}&picture=${meta.imgFacebook}`,
          icon: IconFacebook,
        },
        [serviceIds.LINKED_IN]: {
          name: this.$t('feature.share.service.linkedIn'),
          url: `https://www.linkedin.com/sharing/share-offsite/?url=${meta.shareUrl}`,
          icon: IconLinkedIn,
        },
        [serviceIds.SMS]: {
          name: this.$t('feature.share.service.textMessage'),
          url: `sms://?&body=${meta.body}`,
          icon: IconSMS,
        },
        [serviceIds.TWITTER]: {
          name: this.$t('feature.share.service.twitter'),
          url: `https://twitter.com/intent/tweet?url=${meta.shareUrl}&text=${meta.descriptionShort}&hashtags=${meta.hashTags}`,
          icon: IconTwitter,
        },
        [serviceIds.PINTEREST]: {
          name: this.$t('feature.share.service.pinterest'),
          url: `https://pinterest.com/pin/create/button/?url=${meta.shareUrl}&description=${meta.title}&media=${meta.imgPinterest}`,
          icon: undefined,
        },
        [serviceIds.WHATSAPP]: {
          url: `whatsapp://send/?text=${meta.descriptionShort}%20${meta.shareUrl}`,
          name: this.$t('feature.share.service.whatsapp'),
          icon: IconWhatsapp,
          via: 'location',
        },
      };
    },

    enabledServices() {
      return enabledServiceIds.reduce((enabledServices, serviceId) => {
        const service = this.allServices[serviceId];
        return service ? Object.assign(enabledServices, { [serviceId]: service }) : enabledServices;
      }, {});
    },
  },

  watch: {
    isOpen(isOpen) {
      if (isOpen) {
        this.addClickListeners();
      } else {
        this.removeClickListeners();
      }
    },
  },

  mounted() {
    this.addClickListeners();
  },

  methods: {
    togglePopup() {
      if (this.isOpen) {
        this.isOpen = false;
      } else if (navigator.share) {
        navigator.share({
          title: this.$t('meta.shareTitle'),
          url: this.shareUrl,
        });
      } else {
        this.isOpen = true;
      }
    },

    closePopup() {
      this.isOpen = false;
    },

    shareVia(serviceId) {
      // create url and open
      const service = this.allServices[serviceId];

      if (!service.via) {
        openShareWindow(service.url);
      } else if (service.via === 'location') {
        window.location.href = service.url;
      } else {
        // eslint-disable-next-line
        console.log('Handling of service not defined');
      }

      // TODO: Tracking of share clicks?
    },

    addClickListeners() {
      document.addEventListener('click', this.handleClickOutside, true);

      this.$once('hook:beforeDestroy', () => {
        document.removeEventListener('click', this.handleClickOutside);
      });
    },

    removeClickListeners() {
      document.removeEventListener('click', this.handleClickOutside);
    },

    handleClickOutside(event) {
      if (!this.isOpen || this.$refs.tooltipEl.contains(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      this.isOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.share__toggle-popup {
  line-height: 30px;
  padding: 14px 0;
  display: block;
}

$padding: 20px;
.share__popup {
  @include transparent-layer;
  margin-left: auto;
  margin-right: auto;
  z-index: 1;
  position: absolute;
  max-width: $mobileDefaultMaxWidth;
  left: 50%;
  bottom: 0;
  padding-top: 70px; // save space for close button
  background-color: rgba($color-background, 0.9);
  transform: translateX(-50%);
  width: calc(100vw - 2 * #{$padding});
}

.share__close {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 100;
  width: auto;
}

.share__headline {
  @include font-headline;
  text-align: center;
  margin-bottom: 30px;
}

.share__links-list {
  display: grid;
  grid-template-columns: 50% 50%;
  grid-template-rows: auto;
  grid-row-gap: 10px;
  align-items: stretch;
}

.share__link {
  display: block;
  line-height: 40px;
  transition: color 0.5s ease;

  &:hover {
    color: $color-purple;
  }
}

.share__link-inner {
  display: flex;
  align-items: center;
}

.share__link-icon {
  width: 32px;
  height: 32px;
  margin-right: 10px;
}

.share__link--sms,
.share__link--whatsapp {
  // disable services that don't work on desktop
  @include helper__greater(768px) {
    display: none;
  }
}

.slide-bottom-enter-active,
.slide-bottom-leave-active {
  transition-duration: 0.5s;
  transition-property: opacity transform;
  transition-timing-function: ease;
}

.slide-bottom-enter,
.slide-bottom-leave-active {
  opacity: 0;
  transform: translateX(-50%) translateY(100%);
}
</style>
